import * as breakpoint from '@pixelunion/breakpoint';
import EventHandler from '@pixelunion/events';
import loadYoutubeIframeAPI from '../helpers/youtubeIframeAPIHelper';

export default class MediaManager {
  constructor(el, settings) {
    this.el = el
    this.settings = settings;
    this.figures = this.el.querySelectorAll('[data-product-gallery-figure]');
    this.media = [];
    this.selected = null;
    this._cardsPlayer = null;

    try {
      this.modelJson = JSON.parse(this.el.dataset.productModels);
    } catch (error) {
      console.warn('Unable to parse model data');
    }

    this.events = new EventHandler();

    const features = [];

    if (this.el.querySelector('[data-media-type="model"]')) {
      features.push(
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: () => this._onModelLibraryLoad(),
        },
      );
      features.push(
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: () => this._onShopifyXRLoad(),
        },
      );
    }

    if (this.el.querySelector('[data-media-type="video"]')) {
      Promise.all([
        new Promise(resolve => {
          if (!document.querySelector('#plyr-stylesheet')) {
            const link = document.createElement('link');

            link.setAttribute('id', 'plyr-stylesheet');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css');
            link.onload = resolve;
            document.body.appendChild(link);
          } else {
            resolve();
          }
        }),
        new Promise(resolve => {
          features.push({
            name: 'video-ui',
            version: '1.0',
            onLoad: resolve,
          });
        }),
      ]).then(() => this._onVideoLibraryLoad());
    }

    loadYoutubeIframeAPI().then(() => this._onExternalVideoLibraryLoad());

    if (features.length) {
      Shopify.loadFeatures(features);
    }
  }

  set cardsPlayer(player) {
    this._cardsPlayer = player;
  }

  onMediaSelect(figure) {
    this._pauseMedia();
    this._onMediaSelect(this.media.filter(m => m.figure === figure)[0]);
  }

  unload() {
    this.events.unregisterAll();
    this.media.forEach(m => m.unload());
    this.media = [];
  }

  _onModelLibraryLoad() {
    const onPlayListener = event => {
      const { viewer, modelIsVisible } = event.detail.modelViewerUI;

      // This fixes a weird bug where the viewer UI is out of sync with the
      // model visibility. Hiding and showing the model triggers an intersectionObserver
      // in model-viewer which triggers an event which updated model-viewer-ui and
      // allows the model to be properly interactable. Seems to be an issue when loading
      // quickshops for the 2nd time, and sometimes when changing settings in the TE.
      if (!modelIsVisible) {
        viewer.style.display = 'none';
        setTimeout(() => {
          viewer.style.display = '';
        }, 50);
      }
      if (this._cardsPlayer) {
        this._cardsPlayer.lock();
      }
    };
    const onPauseListener = () => {
      if (this._cardsPlayer) {
        this._cardsPlayer.unlock();
      }
    };

    const modelFigures = this.el.querySelectorAll('[data-media-type="model"]');
    const controls = ['zoom-in', 'zoom-out'];
    if (document.fullscreenEnabled) controls.push('fullscreen');

    modelFigures.forEach(figure => {
      const modelEl = figure.querySelector('model-viewer');
      const player = new Shopify.ModelViewerUI(modelEl, { controls });
      this.media.push({
        type: 'model',
        figure,
        player,
        focus: () => {},
        restart: () => {},
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      });
      this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_play', onPlayListener);
      this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_pause', onPauseListener);
      // This fixes an issue with model-viewer-ui where the touchend event pauses the model,
      // which in turn shows the play/pause button, which is then activated by the click event.
      this.events.register(modelEl, 'touchend', e => {
        if (!event.cancelable) return;
        e.preventDefault();
      });
    });
  }

  _onShopifyXRLoad() {
    if (this.modelJson) {
      window.ShopifyXR = window.ShopifyXR || function() {(ShopifyXR.q = ShopifyXR.q||[] ).push(arguments); }; // eslint-disable-line
      if (window.ShopifyXR.addModels) {
        window.ShopifyXR.addModels(this.modelJson);
      } else {
        window.ShopifyXR('addModels', this.modelJson);
      }
    }
  }

  _onVideoLibraryLoad() {
    const videoFigures = this.el.querySelectorAll('[data-media-type="video"]');

    videoFigures.forEach(videoFigure => {
      const player = new Shopify.Plyr(
        videoFigure.querySelector('video'),
        {
          loop: { active: this.settings.enable_video_looping },
        },
      );
      const plyrEl = videoFigure.querySelector('.plyr');
      const video = {
        type: 'video',
        figure: videoFigure,
        player,
        focus: () => plyrEl.focus(),
        restart: () => {
          player.restart();
          player.play();
        },
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      };

      this.events.register(videoFigure, 'play', () => this._pauseMedia(video));
      this.media.push(video);
    });
  }

  _onExternalVideoLibraryLoad() {
    const videoFigures = this.el.querySelectorAll('[data-media-type="external_video"]');

    videoFigures.forEach(videoFigure => {
      let playerReady = false;
      const player = new YT.Player(videoFigure.querySelector('iframe'), {
        events: {
          onReady: () => { playerReady = true; },
        },
      });
      const video = {
        type: 'video',
        figure: videoFigure,
        player,
        focus: () => player.getIframe().focus(),
        restart: () => {
          if (!playerReady) return;
          player.seekTo(0);
          player.playVideo();
        },
        pause: () => playerReady && player.pauseVideo(),
        play: () => playerReady && player.playVideo(),
        unload: () => player.destroy(),
      };

      player.addEventListener('onStateChange', ({ data }) => {
        if (data === YT.PlayerState.ENDED && this.settings.enable_video_looping) {
          video.restart();
        } else if (data === YT.PlayerState.PLAYING) {
          this._pauseMedia(video);
        }
      });

      this.media.push(video);
    });
  }

  _pauseMedia(excludeMedia = null) {
    this.media.forEach(m => {
      if (m !== excludeMedia) {
        m.pause();
      }
    });
  }

  _onMediaSelect(media) {
    if (!media) return;

    switch (media.type) {
      case 'video':
        media.focus();
        if (this.settings.enable_video_autoplay && breakpoint.min('M')) {
          media.play();
        }
        break;
      case 'model':
        if (document.querySelector('html').classList.contains('no-touch') && breakpoint.min('M')) {
          media.play();
        }
        break;
      default:
        break;
    }
  }
}
